<template>
    <div class="contenedor-principal">
        <h1>El micelio: la red invisible que sostiene el mundo subterráneo</h1>

        <p>Si alguna vez has caminado por el monte pensando que pisabas simple tierra, te sorprenderá lo que hay debajo:
            una extensa red viva, con un papel enorme en los ecosistemas, conocida como <strong>micelio</strong>.</p>

        <h2>¿Qué es el micelio?</h2>
        <p>Cuando pensamos en hongos solemos imaginar una seta, a veces comestible y otras muchas no. Pero la seta es solo
            una pequeña parte del organismo: la estructura que produce las esporas. La mayor parte del hongo suele estar
            escondida en el suelo, la madera u otros materiales, y es el micelio.</p>

        <p>El micelio es la <strong>parte vegetativa del hongo</strong>, y por eso a veces se compara con las raíces y el
            aparato digestivo del organismo. Está formado por una extensa red de filamentos microscópicos llamados
            <strong>hifas</strong>, que nacen de las esporas y se van ramificando y entrelazando.</p>

        <p>Puede llegar a ser inmenso: en Oregón (Estados Unidos), un ejemplar de <em>Armillaria ostoyae</em> ocupa unas
            965 hectáreas, y se calcula que tiene entre 2400 y 8650 años.</p>

        <h2>Un motor de los ecosistemas</h2>
        <p>Muchos hongos descomponen materia orgánica, como la hojarasca o la madera muerta, y así devuelven nutrientes al
            suelo. Además, la red de hifas ayuda a unir las partículas de tierra y a mejorar su estructura.</p>

        <h2>La "Wood Wide Web"</h2>
        <p>Muchos hongos forman <strong>micorrizas</strong>, una asociación con las raíces de la gran mayoría de las plantas
            terrestres, no solo de los árboles. El hongo ayuda a la planta a conseguir agua, nitrógeno, fósforo y otros
            nutrientes y, a cambio, recibe azúcares y otros compuestos de carbono que la planta obtiene con la
            fotosíntesis.</p>

        <p>El micelio también puede conectar las raíces de distintas plantas. A esa red se la llamó <em>Wood Wide Web</em>
            a raíz de un estudio publicado en <em>Nature</em> en 1997, y ha dado lugar a una idea muy atractiva: que los
            árboles se avisan de las plagas y que los grandes alimentan a los pequeños.</p>

        <p>La realidad es más prudente. Algunos experimentos han observado transferencia de carbono y de señales químicas,
            sobre todo con plántulas y en condiciones controladas. Pero una revisión publicada en 2023 concluyó que todavía
            no hay pruebas suficientes para afirmar que esto ocurra de forma generalizada en los bosques, o que beneficie a
            los árboles a través de esa red. Lo que nadie discute es la importancia de las micorrizas para las plantas y el
            suelo.</p>

        <h2>Una mirada hacia el futuro</h2>
        <p>Además de su función en los ecosistemas, los hongos ya tienen varias aplicaciones, y otras siguen en
            investigación:</p>
        <ul>
            <li><strong>Embalajes:</strong> materiales compostables hechos con micelio, como alternativa a algunos plásticos
                de un solo uso.</li>
            <li><strong>Construcción:</strong> paneles aislantes y ladrillos para usos no estructurales.</li>
            <li><strong>Textiles:</strong> materiales similares al cuero para ropa y calzado.</li>
            <li><strong>Limpieza de suelos:</strong> se investiga su capacidad para degradar hidrocarburos y otros
                contaminantes; algunas especies degradan ciertos plásticos en el laboratorio.</li>
            <li><strong>Alimentación:</strong> alternativas a la carne elaboradas con micoproteína.</li>
            <li><strong>Medicina:</strong> desde la histórica penicilina hasta nuevas líneas de investigación.</li>
        </ul>

        <!-- CAJA DE RECURSOS -->
        <div class="caja-recurso">
            <h3>Descubre el micelio con nosotros</h3>
            <p>
                En nuestro taller de <router-link to="/detalle-zalla-natura">Zalla Natura</router-link> hablamos de esta red
                oculta y aprendemos a montar un cultivo de setas ostra para seguir en casa cómo se desarrolla el hongo.
                Recuerda: no consumas ninguna seta silvestre sin que la identifique una persona experta.
            </p>
            <div class="botones-recurso">
                <router-link to="/calendario" class="proximas-actividades">Ver próximas actividades</router-link>
                <router-link to="/contacto?servicio=talleres_municipios" class="btn-ver-mas">Pedir un taller</router-link>
            </div>
            <p class="descargas-titulo"><strong>Láminas para imprimir:</strong></p>
            <div class="botones-recurso">
                <a href="/descargas/laminas/cultivo-setas-es.pdf" download class="btn-ver-mas">Cultivar setas en un bote (PDF)</a>
                <a href="/descargas/laminas/micelio-es.pdf" download class="btn-ver-mas">El micelio (PDF)</a>
                <a href="/descargas/laminas/reproduccion-hongos-es.pdf" download class="btn-ver-mas">Reproducción de los hongos (PDF)</a>
                <router-link to="/servicios/descargas" class="btn-ver-mas">Más láminas, también en euskera</router-link>
            </div>
        </div>

        <!-- REDES SOCIALES -->
        <p class="social-media"><b>¡Síguenos en redes sociales para descubrir más curiosidades sobre la naturaleza de
                nuestro entorno!</b></p>
        <div class="redes-sociales">
            <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer"
                title="Geobizi en Instagram">
                <img src="/imagenes/iconos/instagram.png" alt="Geobizi en Instagram" class="social-icon"
                    loading="lazy" />
            </a>
            <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer"
                title="Geobizi en Facebook">
                <img src="/imagenes/iconos/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
            </a>
            <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer"
                title="Geobizi en TikTok">
                <img src="/imagenes/iconos/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
            </a>
        </div>

        <!-- GALERÍA DE IMÁGENES -->
        <div class="imagen-trio">
            <div>
                <img class="img" src="/imagenes/proyectos/zallanatura/micelio1.avif"
                    alt="Red blanquecina de micelio extendida sobre el sustrato"
                    title="Red de micelio" loading="lazy" />
            </div>
            <div>
                <img class="img" src="/imagenes/proyectos/zallanatura/micelio2.avif"
                    alt="Setas ostra creciendo sobre un sustrato de cultivo"
                    title="Setas ostra" loading="lazy" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const pageUrl = 'https://www.geobizi.com/blog/micelio'
const ogImage = 'https://www.geobizi.com/imagenes/proyectos/zallanatura/micelio1.avif'

useHead({
    title: 'El micelio: qué es y por qué es clave para los ecosistemas | Geobizi',
    meta: [
        { name: 'description', content: 'Descubre qué es el micelio, cómo se relaciona con las raíces de las plantas, qué sabemos realmente de la Wood Wide Web y para qué se usan los hongos hoy.' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Geobizi' },
        { name: 'theme-color', content: '#0b8a4c' },
        { name: 'language', content: 'es' },
        { property: 'og:title', content: 'El micelio: la red invisible que sostiene el mundo subterráneo' },
        { property: 'og:description', content: 'Qué es el micelio, qué papel tiene en los ecosistemas y qué sabemos realmente de la Wood Wide Web.' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: ogImage },
        { name: 'twitter:card', content: 'summary_large_image' }
    ],
    link: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'image_src', href: ogImage }
    ],
    script: [
        {
            type: 'application/ld+json',
            children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": "El micelio: la red invisible que sostiene el mundo subterráneo",
                "description": "Qué es el micelio, qué papel tiene en los ecosistemas y qué sabemos realmente de la Wood Wide Web.",
                "image": ogImage,
                "author": {
                    "@type": "Organization",
                    "name": "Geobizi",
                    "url": "https://www.geobizi.com"
                },
                "publisher": {
                    "@type": "Organization",
                    "name": "Geobizi",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
                    }
                },
                "datePublished": "2026-11-05T08:00:00+01:00",
                "dateModified": "2026-11-05T08:00:00+01:00",
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": pageUrl
                }
            })
        }
    ]
})
</script>

<style scoped>
/* CAJA DE RECURSOS */
.caja-recurso {
    background-color: var(--megashoftgreen);
    border-left: 4px solid var(--shoftgreen);
    padding: 1.5rem;
    margin: 2.5rem 0;
    border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
    margin-top: 0;
    color: var(--lightgreen);
    font-size: 1.2rem;
}

.caja-recurso p {
    margin-bottom: 1.5rem;
    color: var(--darkgrey);
}

.caja-recurso .descargas-titulo {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
}

.botones-recurso {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.proximas-actividades {
    background-color: var(--supershoftgreen);
    color: var(--lightgreen);
    padding: 0.6rem 1.2rem;
    text-decoration: none;
    font-weight: bold;
    border-radius: 4px;
    transition: background-color 0.3s;
}

.proximas-actividades:hover {
    background-color: var(--shoftgreen);
    color: var(--white);
}

.btn-ver-mas {
    background-color: transparent;
    color: var(--lightgreen);
    padding: 0.6rem 1.2rem;
    text-decoration: none;
    font-weight: bold;
    border: 1px solid var(--shoftgreen);
    border-radius: 4px;
    transition: all 0.3s;
}

.btn-ver-mas:hover {
    background-color: var(--shoftgreen);
    color: white;
}

/* REDES SOCIALES */
.social-media {
    margin-top: 2rem;
    color: var(--darkgrey);
}

.redes-sociales {
    margin-left: 1rem;
    display: flex;
    align-items: center;
}

.social-icon {
    width: 2.5rem;
    margin-right: 1rem;
    margin-bottom: 1.5rem;
    transition: transform 0.3s;
}

.social-icon:hover {
    transform: scale(1.1);
}
</style>
